import { useState, useEffect, useCallback, useRef } from "react";
import { useTablePreferences } from "./utils/useTablePreferences";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { fetchAllNotifications } from "../pages/services/notificationService";
import NotificationDetailsDialog from "./NotificationDetailsDialog";
import {
  TypeBadge, EntityBadge, statusSummary, formatDateTime,
} from "./notificationDisplay";
import "../styles/ListPage.css";
import "./NotificationManagement.css";

const TYPE_OPTIONS = [
  { value: "email", label: "✉ Email" },
  { value: "whatsapp", label: "💬 WhatsApp" },
];

const STUDENT_SEARCH_DEBOUNCE_MS = 300;

const NotificationManagement = () => {
  const API_URL = process.env.REACT_APP_API_URL;

  const { rowsPerPage, setRowsPerPage } = useTablePreferences("table_prefs_notifications", 20);
  const [notifications, setNotifications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState(null);
  const [batchFilter, setBatchFilter] = useState(null);
  const [studentFilter, setStudentFilter] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [batchOptions, setBatchOptions] = useState([]);

  const [selectedNotification, setSelectedNotification] = useState(null);

  const filterChangedRef = useRef(false);
  const studentSearchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        search: searchTerm.trim(),
        type: typeFilter?.value,
        batchId: batchFilter?.value,
        studentId: studentFilter?.value,
        dateFrom,
        dateTo,
      };
      const res = await fetchAllNotifications(filters, page + 1, rowsPerPage);
      setNotifications(res.data || []);
      setTotalCount(res.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to load notifications:", err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, typeFilter, batchFilter, studentFilter, dateFrom, dateTo, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (filterChangedRef.current) {
      setPage(0);
      filterChangedRef.current = false;
    }
  }, [searchTerm, typeFilter, batchFilter, studentFilter, dateFrom, dateTo]);

  useEffect(() => {
    const loadBatches = async () => {
      try {
        const res = await fetch(`${API_URL}/api/batches`);
        const response = await res.json();
        setBatchOptions((response.data || []).map((b) => ({ value: b._id, label: b.batchTitle })));
      } catch (err) { console.error("Error fetching batches:", err); }
    };
    loadBatches();
  }, [API_URL]);

  const loadStudentOptions = (inputValue) => new Promise((resolve) => {
    clearTimeout(studentSearchTimer.current);
    const term = (inputValue || "").trim();
    if (!term) { resolve([]); return; }
    studentSearchTimer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ page: 1, pageSize: 20, search: term });
        const res = await fetch(`${API_URL}/api/student?${params}`);
        const response = await res.json();
        resolve((response.data || []).map((s) => ({ value: s._id, label: s.studentName })));
      } catch {
        resolve([]);
      }
    }, STUDENT_SEARCH_DEBOUNCE_MS);
  });

  const markFilterChanged = () => { filterChangedRef.current = true; };

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  return (
    <div className="lp-page">
      <div className="lp-header">
        <div className="lp-header-left">
          <h2 className="lp-title">Notification Management</h2>
          <span className="lp-count">{totalCount} records</span>
        </div>
      </div>

      <div className="lp-toolbar">
        <div className="lp-search-wrap">
          <span className="lp-search-icon">&#128269;</span>
          <input
            type="text"
            placeholder="Search by subject, body, recipient…"
            value={searchTerm}
            onChange={(e) => { markFilterChanged(); setSearchTerm(e.target.value); }}
            className="lp-search"
          />
        </div>

        <div className="nm-filters">
          <Select
            options={TYPE_OPTIONS}
            value={typeFilter}
            onChange={(opt) => { markFilterChanged(); setTypeFilter(opt); }}
            isClearable
            isSearchable
            placeholder="Type"
            className="nm-select"
            classNamePrefix="nm-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <Select
            options={batchOptions}
            value={batchFilter}
            onChange={(opt) => { markFilterChanged(); setBatchFilter(opt); setStudentFilter(null); }}
            isClearable
            isSearchable
            placeholder="Batch"
            className="nm-select"
            classNamePrefix="nm-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <AsyncSelect
            value={studentFilter}
            onChange={(opt) => { markFilterChanged(); setStudentFilter(opt); setBatchFilter(null); }}
            loadOptions={loadStudentOptions}
            cacheOptions
            isClearable
            placeholder="Search student…"
            noOptionsMessage={({ inputValue }) => (inputValue ? "No students found" : "Type to search students")}
            loadingMessage={() => "Searching…"}
            className="nm-select"
            classNamePrefix="nm-select"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <div className="nm-date-range">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { markFilterChanged(); setDateFrom(e.target.value); }}
              aria-label="Sent from date"
            />
            <span className="nm-date-sep">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { markFilterChanged(); setDateTo(e.target.value); }}
              aria-label="Sent to date"
            />
          </div>
        </div>
      </div>

      <div className="lp-table-card">
        {loading ? (
          <div className="lp-state"><div className="lp-spinner" /><p>Loading notifications…</p></div>
        ) : notifications.length === 0 ? (
          <div className="lp-state"><p>No notifications found.</p></div>
        ) : (
          <table className="lp-table">
            <thead>
              <tr>
                <th>Sent At</th>
                <th>Type</th>
                <th>Sent To</th>
                <th>Subject / Template</th>
                <th style={{ textAlign: "center" }}>Recipients</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map((notif) => (
                <tr key={notif._id} onClick={() => setSelectedNotification(notif)}>
                  <td className="nm-nowrap">{formatDateTime(notif.sentAt)}</td>
                  <td><TypeBadge type={notif.type} /></td>
                  <td>
                    <div className="nm-entity-cell">
                      <EntityBadge entityType={notif.entityType} />
                      <span className="nm-entity-name">{notif.entityName || "—"}</span>
                    </div>
                  </td>
                  <td className="nm-subject-cell">
                    <span className="nm-subject">{notif.subject || notif.templateName || "—"}</span>
                    {notif.bodyPreview && <span className="nm-preview">{notif.bodyPreview}</span>}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <span className="nm-sent-count">{notif.sentCount}</span>
                    {notif.failedCount > 0 && <span className="nm-failed-count"> / {notif.failedCount} failed</span>}
                  </td>
                  <td>
                    <div className="nm-status-cell">{statusSummary(notif)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && notifications.length > 0 && (
        <div className="lp-pagination">
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0}>← Previous</button>
          <span className="lp-page-info">Page {page + 1} of {totalPages}</span>
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))} disabled={page >= totalPages - 1}>Next →</button>
          <div className="lp-page-size">
            <label htmlFor="nm-pageSize">Rows:</label>
            <select id="nm-pageSize" value={rowsPerPage} onChange={(e) => { setPage(0); setRowsPerPage(Number(e.target.value)); }}>
              {[10, 20, 30, 50].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}

      <NotificationDetailsDialog
        notification={selectedNotification}
        isOpen={!!selectedNotification}
        onClose={() => setSelectedNotification(null)}
        onResendSuccess={load}
      />
    </div>
  );
};

export default NotificationManagement;
